import { useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import FormDialog from '@sayyesschool/frontend/components/form-dialog';
import LoadingIndicator from '@sayyesschool/frontend/components/loading-indicator';
import Page from '@sayyesschool/frontend/components/page';
import { usePayment, usePayments } from '@sayyesschool/frontend/features/payments';
import { useBoolean } from '@sayyesschool/frontend/hooks/state';

import PaymentForm from '@/components/payments/payment-form';
import PaymentSearchForm from '@/components/payments/payment-search-form';
import PaymentsTable from '@/components/payments/payments-table';

export default function PaymentsPage() {
    const params = useParams();
    const navigate = useNavigate();
    const [payments, actions] = usePayments();
    const [payment] = usePayment(params.id);

    const [isPaymentFormOpen, togglePaymentFormOpen] = useBoolean(false);
    const [isLoading, setLoading] = useBoolean(false);

    useEffect(() => {
        if (!payment?.id) return;

        togglePaymentFormOpen(true);
    }, [payment?.id, togglePaymentFormOpen]);

    const handleClose = useCallback(() => {
        actions.unsetPayment();
        togglePaymentFormOpen(false);
        navigate('/payments');
    }, [actions, togglePaymentFormOpen, navigate]);

    const handleSubmit = useCallback(data => {
        const promise = payment
            ? actions.updatePayment(payment.id, data)
            : actions.createPayment(data);

        return promise.then(handleClose);
    }, [payment, actions, handleClose]);

    const handleResolve = useCallback(payment => {
        return actions.resolvePayment(payment.uuid);
    }, [actions]);

    const handleDelete = useCallback(payment => {
        return actions.deletePayment(payment.id);
    }, [actions]);

    const handleEdit = useCallback(payment => {
        actions.setPayment(payment);
        togglePaymentFormOpen(true);
    }, [actions, togglePaymentFormOpen]);

    const handleSearch = useCallback(params => {
        setLoading(true);

        return actions.getPayments(params).finally(() => setLoading(false));
    }, [actions, setLoading]);

    if (!payments) return <LoadingIndicator />;

    const sortedPayments = [...payments].sort((a, b) => new Date(b.date) - new Date(a.date));

    return (
        <Page id="payments">
            <Page.Header
                title="Платежи"
                actions={[{
                    key: 'add',
                    icon: 'add',
                    content: 'Создать платеж',
                    color: 'primary',
                    variant: 'solid',
                    onClick: togglePaymentFormOpen
                }]}
                loading={isLoading}
            />

            <Page.Content>
                <PaymentSearchForm onSubmit={handleSearch} />

                <Page.Section
                    variant="outlined"
                    compact
                >
                    <PaymentsTable
                        payments={sortedPayments}
                        onResolve={handleResolve}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                    />
                </Page.Section>
            </Page.Content>

            <FormDialog
                form="payment-form"
                title={payment ? 'Редактирование платежа' : 'Новый платеж'}
                open={isPaymentFormOpen}
                onClose={handleClose}
            >
                <PaymentForm
                    id="payment-form"
                    payment={payment || undefined}
                    onSubmit={handleSubmit}
                />
            </FormDialog>
        </Page>
    );
}